import { useEffect, useMemo, useState } from 'react'
import { DateTime, Duration } from 'luxon'
import { Durations } from '@/constants/durations'

interface CountdownOptions {
  format?: ToHumanOptions

  /**
   * The number of units to display in the countdown. This also affects the precision of the countdown.
   * Minimum value is 1, maximum value is 5.
   * The unit resolution includes months, days, hours, minutes, and seconds.
   *
   * Example 1: If set to 2, and if there is more than 1 month remaining, the result will show months and days remaining,
   * the result will be rounded down to the nearest day, and the hook will only return a new value when the day changes.
   * Example 2: If set to 2, and if there are 30 minutes remaining, the result will show minutes and seconds remaining,
   * the result will be rounded down to the nearest second, and the hook will only return a new value when the second changes.
   * Example 3: If set to 5, the result will show months, days, hours, minutes, and seconds remaining
   * rounded down to the nearest second, and the hook will return a new value every second.
   * @default 2
   */
  numUnits?: number
}

interface Args extends CountdownOptions {
  skip?: boolean
  endDate: number | string | undefined
}

interface UseCountdownResult {
  countdown?: Duration
  formattedCountdown: string
  isExpired: boolean
}

export const useCountDown = ({ endDate, skip, format, numUnits }: Args): UseCountdownResult | null => {
  const end = useMemo(() => {
    const d = typeof endDate !== 'undefined' ? new Date(endDate) : new Date()
    return DateTime.fromJSDate(d)
  }, [endDate])

  const [result, setResult] = useState<UseCountdownResult | null>(null)

  useEffect(() => {
    if (skip) return

    setResult(computeCountdown(end, { format, numUnits }))
    const interval = setInterval(() => {
      setResult((prev) => {
        const newResult = computeCountdown(end, { format, numUnits })
        if (!prev) return newResult
        if (prev.formattedCountdown === newResult.formattedCountdown) return prev
        return newResult
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [skip, end, format, numUnits])

  return result
}

/**
 * This type is not importing correctly for some reason and is causing build errors, so created a copy.
 */
interface ToHumanOptions {
  listStyle?: 'long' | 'short' | 'narrow' | undefined
  unitDisplay?: 'long' | 'short' | 'narrow' | undefined
  roundingPriority?: 'auto' | 'morePrecision' | 'lessPrecision' | undefined
  roundingIncrement?: 1 | 2 | 5 | 10 | 20 | 25 | 50 | 100 | 200 | 250 | 500 | 1000 | 2000 | 2500 | 5000 | undefined
  roundingMode?:
    | 'ceil'
    | 'floor'
    | 'expand'
    | 'trunc'
    | 'halfCeil'
    | 'halfFloor'
    | 'halfExpand'
    | 'halfTrunc'
    | 'halfEven'
    | undefined
  trailingZeroDisplay?: 'auto' | 'stripIfInteger' | undefined
  maximumFractionDigits?: number
}

const UNITS = ['months', 'days', 'hours', 'minutes', 'seconds'] as const

function computeCountdown(end: DateTime, options?: CountdownOptions): UseCountdownResult {
  if (!end.isValid) {
    return {
      isExpired: true,
      formattedCountdown: '',
    }
  }

  const num = options?.numUnits || 2
  const numUnits = num > UNITS.length ? UNITS.length : num < 1 ? 1 : num
  const unitsIdx = getUnitsIndex(end)
  const unitsToUse = UNITS.slice(unitsIdx, unitsIdx + numUnits)

  const countdown = end.diffNow(unitsToUse)

  if (!countdown.isValid) {
    return {
      isExpired: true,
      countdown,
      formattedCountdown: '',
    }
  }

  const isExpired = end <= DateTime.now()
  const toHumanOpts: ToHumanOptions = { maximumFractionDigits: 0, roundingMode: 'floor', ...options?.format }

  return {
    countdown,
    formattedCountdown: countdown.toHuman(toHumanOpts),
    isExpired,
  }
}

function getUnitsIndex(end: DateTime): number {
  const msDiff = end.diffNow()
  if (msDiff.milliseconds >= Durations.ONE_MONTH_IN_MILLISECONDS) return 0 // UNITS.indexOf('months')
  if (msDiff.milliseconds >= Durations.ONE_DAY_IN_MILLISECONDS) return 1 // UNITS.indexOf('days')
  if (msDiff.milliseconds >= Durations.ONE_HOUR_IN_MILLISECONDS) return 2 // UNITS.indexOf('hours')
  if (msDiff.milliseconds >= Durations.ONE_MINUTE_IN_MILLISECONDS) return 3 // UNITS.indexOf('minutes')
  return 4 // UNITS.indexOf('seconds')
}
